// screenVolume — the world's volume knobs, and the hands that turn them.
//
// Khari, 2026-08-04: "Some items may be on, and background noise... it may play
// at a lower volume while the user is not using it... and then news comes on and
// an NPC says 'hey turn that up', and another character turns the volume up
// (sliding up the range programatically)."
//
// THE SLIDE IS THE WHOLE POINT. A volume that jumps is a bug in a scene: the
// line is "turn that up", and what you should hear is a hand on a dial. So a
// ramp is a target plus a duration, stepped by the render loop, and a screen
// always knows its way back to its resting level.
//
// This module holds NO audio. It holds numbers, because the audio is shared:
// every screen showing a given show plays ONE wall-clock-synced stream, so
// per-screen volume cannot live on per-screen elements. Screens publish a level,
// the mixer takes the loudest audible one (see newsPodcast.mixLevels), and that
// is what a viewer hears — a salon TV murmuring at 0.25 until you walk up to a
// jumbotron at 1.0, which then wins.
//
// It is deliberately outside React so a dialogue cue, an NPC, or the ensemble
// director can turn a screen up without holding a component reference.

// placementId → { base, target, current, perMs, until }
const knobs = new Map();
const subs = new Set();

function knobFor(id) {
  let k = knobs.get(id);
  if (!k) { k = { base: 1, target: null, current: null, perMs: 0, restoreAt: 0 }; knobs.set(id, k); }
  return k;
}

/** The resting level from the placement row. Idempotent; safe every render. */
export function setBaseVolume(id, base) {
  const k = knobFor(id);
  const b = Math.max(0, Math.min(1, Number(base) ?? 1));
  if (k.base === b) return;
  k.base = b;
  // A knob nobody has touched follows its row. One mid-ramp does not — otherwise
  // saving the panel would yank the volume out from under a playing scene.
  if (k.target === null) k.current = null;
}

/**
 * Turn a screen up (or down), over time.
 *
 * `ms` is the length of the SLIDE, not a delay — 900ms reads as a hand moving,
 * 0 reads as a fault. `holdMs` returns it to its resting level afterwards, which
 * is what a scene almost always wants: the news gets loud for the item and the
 * room goes back to normal without the script having to remember to undo it.
 * Pass holdMs = 0 to leave it turned up until something else moves it.
 */
export function rampScreen(id, to, ms = 900, holdMs = 0) {
  const k = knobFor(id);
  const target = Math.max(0, Math.min(1, Number(to) || 0));
  const from = k.current == null ? k.base : k.current;
  k.current = from;
  k.target = target;
  k.perMs = ms > 0 ? Math.abs(target - from) / ms : Infinity;
  k.restoreAt = holdMs > 0 ? Date.now() + ms + holdMs : 0;
  console.log(`[screen] #${id} ${from.toFixed(2)} → ${target.toFixed(2)} over ${ms}ms`
    + (holdMs ? `, back in ${holdMs}ms` : ''));
  subs.forEach((fn) => { try { fn(id); } catch { /* noop */ } });
}

/** Put a screen back to its row's resting level, over `ms`. */
export function restoreScreen(id, ms = 900) {
  const k = knobs.get(id);
  if (!k) return;
  rampScreen(id, k.base, ms, 0);
  k.target = null;   // arriving means "resting", not "held at a value"
  k.restoreAt = 0;
}

/**
 * Step every knob.
 *
 * SELF-THROTTLING, so every screen can call it from its own frame loop without
 * any of them being elected to own the tick — a second call inside the same
 * frame returns immediately, and the ramp advances once. The alternative (one
 * designated ticker) breaks the moment that particular screen unmounts, which
 * is exactly when somebody walks out of the room mid-ramp.
 */
let _lastTick = 0;
export function tickScreens() {
  const now = Date.now();
  if (now - _lastTick < 8) return;              // already stepped this frame
  // Clamped: a backgrounded tab returns with a huge gap, and an unclamped step
  // would snap every ramp to its target instead of resuming the slide.
  const dtMs = _lastTick ? Math.min(now - _lastTick, 100) : 16;
  _lastTick = now;
  knobs.forEach((k, id) => {
    if (k.restoreAt && now >= k.restoreAt) {
      k.restoreAt = 0;
      const from = k.current == null ? k.base : k.current;
      k.target = k.base;
      k.perMs = Math.abs(k.base - from) / 900;
      console.log(`[screen] #${id} settling back to ${k.base.toFixed(2)}`);
    }
    if (k.target == null || k.current == null) return;
    const gap = k.target - k.current;
    if (Math.abs(gap) < 0.005) {
      k.current = k.target;
      // Landed on the resting level → hand control back to the row, so a later
      // edit in the panel takes effect instead of being pinned by a stale hold.
      if (!k.restoreAt && Math.abs(k.current - k.base) < 0.005) { k.target = null; k.current = null; }
      return;
    }
    const step = k.perMs === Infinity ? Math.abs(gap) : k.perMs * dtMs;
    k.current += Math.sign(gap) * Math.min(step, Math.abs(gap));
  });
}

/** What this screen's knob is set to right now. */
export function screenVolume(id, base = 1) {
  const k = knobs.get(id);
  if (!k) return Math.max(0, Math.min(1, Number(base) ?? 1));
  return k.current == null ? k.base : k.current;
}

export function onScreenVolume(fn) { subs.add(fn); return () => subs.delete(fn); }

// A scene says "turn that up" without knowing a placement id — it knows the ROOM
// it is playing in. The layer registers what is audible where, so a cue can name
// a room and a kind ("the news, in the salon") and this resolves it.
const registry = new Map();   // placementId → { zone, kind }

export function registerScreen(id, zone, kind) { registry.set(id, { zone, kind: kind || null }); }
export function unregisterScreen(id) { registry.delete(id); knobs.delete(id); }

/**
 * Turn up every screen in a room — the cue an NPC line actually maps to.
 * `kind` narrows it to one show ('news' | 'interview') when a room has both.
 * Returns how many it moved, so a scene can tell "nobody turned it up" from
 * "there was no television".
 */
export function rampRoom(zone, to, { ms = 900, holdMs = 0, kind = null } = {}) {
  let n = 0;
  registry.forEach((meta, id) => {
    if (meta.zone !== zone) return;
    if (kind && meta.kind !== kind) return;
    rampScreen(id, to, ms, holdMs);
    n += 1;
  });
  if (!n) console.warn(`[screen] "turn it up" in "${zone}"${kind ? ` (${kind})` : ''} — no screen there to turn up`);
  return n;
}

// Reachable from the console and from any non-React caller (dialogue cues, the
// ensemble director, an NPC's `do` field), mirroring `world.stage()`.
if (typeof window !== 'undefined') {
  window.screens = {
    up: (zone, to = 0.9, ms = 900, holdMs = 0) => rampRoom(zone, to, { ms, holdMs }),
    set: rampScreen,
    restore: restoreScreen,
    list: () => [...registry.entries()].map(([id, m]) => ({ id, ...m, volume: screenVolume(id) })),
  };
}
